<template>
  <form-modal
    :ref="modalName"
    :name="modalName"
    class="PremiumPlan"
    title="Plan Details"
    submit-name="Go to Checkout"
    @submit="submit"
    @[Events.AFTER_CLOSE]="modalClosed"
  >
    <lm-loading v-if="!planDetailsReady" />
    <div v-else class="h-full flex flex-col">
      <ul class="DetailsList space-y-6">
        <li>
          <span>Billing Cycle</span>
          <ul class="flex">
            <li
              v-for="plan in planDetailsFiltered"
              :key="plan.id"
              class="ml-4"
              @change="changePlan(plan.id)"
            >
              <input
                :id="plan.id"
                type="radio"
                class="cursor-pointer"
                :value="plan.id"
                :checked="plan.id === selectedPlanId"
              />
              <label :for="plan.id" class="pl-2 cursor-pointer capitalize">{{
                plan.label
              }}</label>
            </li>
          </ul>
        </li>
        <li>
          <span>Price per unique user</span>
          <span>
            <span
              v-if="selectedPlan.label === PlanCycle.YEARLY"
              class="line-through mr-1px"
              >{{ costNoDiscount }}</span
            >
            {{ selectedPlan.costPerUser }}€/{{ selectedPlan.interval }}</span
          >
        </li>
        <li :class="{'items-center': uniqueUsers === 1}">
          <div>
            <span>User slots</span>
            <span
              v-if="uniqueUsers > 1"
              class="block mt-1 text-xxs text-gray-500 leading-tight"
            >
              The minimum slots must be equal to the users in your Landscapes
              (<span class="text-gray-900">{{ uniqueUsers }}</span
              >)<br /><router-link :to="{name: 'users'}"
                >Manage Users</router-link
              >
              if you need to reduce this number
            </span>
          </div>
          <lm-input-spinner v-model="slots" :min-value="uniqueUsers" />
        </li>
        <li
          class="mt-2 -mx-4 desktop:-mx-6 px-6 py-4 items-center bg-highlightBaseLight"
        >
          <span>Total cost</span>
          <div class="text-right">
            <span class="text-highlight"
              >{{ totalCost }}€/{{ selectedPlan.interval }}</span
            >
            <span class="block mt-2px text-xxs text-gray-500"
              >+ applicable tax</span
            >
          </div>
        </li>
      </ul>
      <ul class="FAQ mt-6 text-gray-700 text-xs space-y-4 leading-snug">
        <li v-if="trialDaysLeft > 0">
          We will charge your credit card once your trial period expires ({{
            trialDaysLeft
          }}
          days left).
        </li>
        <template v-if="selectedPlan.label === PlanCycle.MONTHLY">
          <li>
            This subscription will be renewed every month. You can cancel
            anytime.
          </li>
          <li>
            If you add or drop new users, we will charge only for the days in
            the period that these users have been members of your landscapes.
            This adjustment will show up in your next month’s invoice.
          </li>
        </template>
        <template v-if="selectedPlan.label === PlanCycle.YEARLY">
          <li>
            This subscription will be renewed a year from now. You can cancel
            Automatic Renewal anytime. Your subscription will remain active
            through the end of the billing period that you’ve already paid for.
          </li>
          <li>
            If you add new users, we will make a one-time, prorated charge to
            your credit card to cover the new user account for the remainder of
            the current billing period.
          </li>
        </template>
      </ul>
      <div class="flex mt-auto p-2 border border-dashed border-warning">
        <lm-icon
          icon="warning"
          size="21"
          class="text-warning flex-shrink-0 mr-2"
        />
        <p class="mb0-important text-xs text-gray-700">
          If you need to add <span class="font-medium">VAT</span> to your
          invoices, please make sure to add all your billing details
          <span class="font-medium">before</span> hitting the Checkout button.
          <span class="cursor-pointer text-link" @click="openBillingModal"
            >Add Billing Info</span
          >
        </p>
      </div>
    </div>
  </form-modal>
</template>

<script>
import LmIcon from '@/components/ui/LmIcon'
import LmLoading from '@/components/ui/LmLoading'
import {mapGetters} from 'vuex'
import {Events, Modal, LocalStorage, PlanCycle} from '@/lib/definitions'
import EventHub from '@/lib/eventHub'
import FormModal from '@/components/modals/FormModal'
import LmInputSpinner from '@/components/ui/LmInputSpinner'

export default {
  name: 'ModalPremiumPlan',
  components: {
    LmIcon,
    LmLoading,
    LmInputSpinner,
    FormModal
  },
  data() {
    return {
      Events,
      PlanCycle,
      modalName: Modal.PREMIUM_PLAN,
      uniqueUsers: null,
      slots: 1
    }
  },
  computed: {
    ...mapGetters([
      'plan',
      'trialDaysLeft',
      'selectedPlanId',
      'planDetails',
      'isPremium',
      'isPremiumYearly'
    ]),
    selectedPlan() {
      if (this.showOnlyYearly) {
        const plan = this.getYearlyPlan()
        this.changePlan(plan.id)
        return plan
      }
      return this.planDetails.find(p => p.id === this.selectedPlanId)
    },
    planDetailsReady() {
      return this.planDetails.length > 0
    },
    planDetailsFiltered() {
      if (this.showOnlyYearly) return [this.getYearlyPlan()]
      else return this.planDetails
    },
    totalCost() {
      return this.slots * this.selectedPlan.costPerUser
    },
    costNoDiscount() {
      const cost = this.selectedPlan.costPerUser
      return (cost * 100) / (100 - this.selectedPlan.percentDiscount)
    },
    showOnlyYearly() {
      return this.isPremium && !this.isPremiumYearly
    }
  },
  watch: {
    planDetails() {
      this.init()
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      if (!this.planDetailsReady) return
      this.uniqueUsers = this.plan.uniqueUsers || 1
      this.slots = this.uniqueUsers
      // Return from Checkout
      if (this.$route.query.cancel) {
        const checkoutData = localStorage.getItem(LocalStorage.CHECKOUT_DATA)
        if (checkoutData) {
          const checkoutDataJson = JSON.parse(checkoutData)
          this.slots = checkoutDataJson.slots
          this.changePlan(checkoutDataJson.planId)
        }
      }
    },
    changePlan(planId) {
      this.$store.commit('setPlanId', planId)
    },
    getYearlyPlan() {
      return this.planDetails.find(p => p.label === PlanCycle.YEARLY)
    },
    openBillingModal() {
      this.$router.pushQuery({submodal: Modal.BILLING_NEXT})
    },
    submit() {
      const data = {
        slots: this.slots,
        planId: this.selectedPlan.id
      }
      EventHub.emit(Events.STRIPE_CHECKOUT, data)
    },
    modalClosed() {
      this.$router.removeKeyFromQuery('cancel')
    }
  }
}
</script>

<style>
@media (--desktop) {
  .PremiumPlan .Modal-wrapper {
    width: 540px;
    .Modal-body {
      height: 480px;
    }
  }
}
</style>
<style scoped>
.DetailsList > li {
  @apply flex justify-between text-gray-900;
}
</style>
